/**
 * JAISIU-3503 / processing-transparency — always-visible Stop / Retry
 * control bar.
 *
 * The chat composer already shows a Stop button that morphs from the
 * Send button while a run is in flight. Operators asked for a
 * *prominent, dedicated* Stop affordance that's visible without
 * hunting for it on the composer, plus a Retry button when the most
 * recent run failed (e.g. transient gateway blip, network drop,
 * tool-timeout). This module renders that bar at the top of the
 * chat thread so the controls are one glance away during long runs.
 *
 * Visibility rules:
 *   - `canAbort` true  → render the bar with a Stop button (and Retry
 *     button if a `lastRunFailed` flag is also true).
 *   - `canAbort` false + `lastRunFailed` true → render a single Retry
 *     button so the operator can re-fire the last prompt.
 *   - Both false → render nothing (the bar is collapsed; the composer
 *     handles its own Send button).
 *
 * The bar is intentionally thin (one line + a button) so it never
 * steals vertical space from the transcript. The Stop button uses
 * the existing `power` icon and the existing `onAbort` callback so
 * the host doesn't need a new wiring path.
 */
import { html, nothing } from "lit";
import { icons } from "../icons.ts";

export function renderRunControlBar(params: {
  canAbort: boolean;
  canStopThinking: boolean;
  lastRunFailed: boolean;
  retryLabel?: string | null;
  retrySecondsUntilReady?: number | null;
  onAbort: () => void;
  onStopThinking: (() => void) | null;
  onRetry: (() => void) | null;
  /** Stable test id; defaults to "chat-run-control-bar". */
  testId?: string;
  /** ARIA label for the bar wrapper; defaults to "Run controls". */
  ariaLabel?: string;
}) {
  const {
    canAbort,
    canStopThinking,
    lastRunFailed,
    retryLabel,
    retrySecondsUntilReady,
    onAbort,
    onStopThinking,
    onRetry,
    testId = "chat-run-control-bar",
    ariaLabel = "Run controls",
  } = params;
  const retryReady =
    retrySecondsUntilReady == null || retrySecondsUntilReady <= 0;
  const retryDisabled = !retryReady || !params.onRetry;
  const retryTitle = !params.onRetry
    ? "Retry is not available for this run."
    : !retryReady
      ? `Retrying in ${retrySecondsUntilReady}s…`
      : (retryLabel ?? "Retry the last prompt");
  if (!canAbort && !lastRunFailed) {
    return nothing;
  }
  return html`
    <div
      class="chat-run-control-bar ${canAbort ? "chat-run-control-bar--live" : "chat-run-control-bar--failed"}"
      role="toolbar"
      aria-label=${ariaLabel}
      data-testid=${testId}
    >
      <div class="chat-run-control-bar__status" aria-live="polite">
        ${
          canAbort
            ? html`
                <span class="chat-run-control-bar__pulse" aria-hidden="true"></span>
                <span>Run in progress</span>
              `
            : lastRunFailed
              ? html`
                  <span class="chat-run-control-bar__warn" aria-hidden="true">${icons.xCircle}</span>
                  <span>Last run failed</span>
                `
              : nothing
        }
      </div>
      <div class="chat-run-control-bar__actions">
        ${
          onRetry
            ? html`
                <button
                  type="button"
                  class="chat-run-control-bar__retry"
                  data-testid="${testId}-retry"
                  ?disabled=${retryDisabled}
                  title=${retryTitle}
                  aria-label=${retryTitle}
                  @click=${() => {
                    if (retryDisabled) return;
                    params.onRetry?.();
                  }}
                >
                  ${icons.refreshCw}
                  <span>Retry</span>
                  ${
                    !retryReady
                      ? html`<span class="chat-run-control-bar__countdown" aria-hidden="true">
                          ${retrySecondsUntilReady}s
                        </span>`
                      : nothing
                  }
                </button>
              `
            : nothing
        }
        ${
          canAbort
            ? html`
                <button
                  type="button"
                  class="chat-run-control-bar__stop"
                  data-testid="${testId}-stop"
                  title=${
                    canStopThinking
                      ? "Stop the current agent run. Shift-click to also revert code changes from this prompt."
                      : "Stop the current agent run (Escape)"
                  }
                  aria-label=${
                    canStopThinking
                      ? "Stop agent run (Shift-click to revert code changes)"
                      : "Stop agent run"
                  }
                  @click=${(e: MouseEvent) => {
                    if (e.shiftKey && canStopThinking) {
                      onStopThinking?.();
                    } else {
                      onAbort();
                    }
                  }}
                >
                  ${icons.power}
                  <span>Stop</span>
                </button>
              `
            : nothing
        }
      </div>
    </div>
  `;
}
